import type {
  BookingPolicy,
  CancellationTier,
  ClassroomAddress,
  ClassroomBooking,
  PricingBreakdownLine,
  ReceiptTicket,
  SessionDate,
} from "./types";
import { safeTimezone, viewerTimezone } from "../../timezones";
import { googleCalendarLink } from "../../calendar";

// The manage page's wording of dates, money, links and the policy. v1 did
// this with @obby/lib's moment helpers (Session__DisplayDateTimeOffset,
// UTCDate__GoogleCalendarFormattedDate); these are the
// same rules on native Intl, which is what the rest of this package uses,
// and times are 24-hour (no AM/PM) per the repo's UI convention.

/**
 * "Sat 5 September, 10:00 – 11:30", plus the zone ("(BST)") when an
 * in-person class is in a different timezone from the visitor. An online
 * class is shown in the visitor's own time, as v1 does.
 */
export function sessionLabel(
  date: SessionDate,
  opts: { timezone?: string; isOnline?: boolean; localTimezone?: string },
): string {
  const local = opts.localTimezone ?? viewerTimezone();
  const tz = opts.isOnline ? local : safeTimezone(opts.timezone);
  const start = new Date(date.startDateTime);
  if (Number.isNaN(start.getTime())) return "";
  const end = new Date(start.getTime() + (date.duration || 0) * 60 * 1000);
  const dayParts = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "long", timeZone: tz }).formatToParts(start);
  const dayPart = (type: Intl.DateTimeFormatPartTypes) => dayParts.find((p) => p.type === type)?.value ?? "";
  const day = `${dayPart("weekday")} ${dayPart("day")} ${dayPart("month")}`;
  const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: tz });
  let label = `${day}, ${time.format(start)} – ${time.format(end)}`;
  if (!opts.isOnline && tz !== local) {
    const zone = new Intl.DateTimeFormat("en-GB", { timeZoneName: "short", timeZone: tz })
      .formatToParts(start)
      .find((p) => p.type === "timeZoneName")?.value;
    if (zone) label += ` (${zone})`;
  }
  return label;
}

/** "5 Nov 2026" in the booking's timezone (due dates). */
export function shortDate(iso: string, timezone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: safeTimezone(timezone) });
}

/** "Thursday 5 November 2026" (or "5 November 2026") in the booking's
 * timezone. */
export function longDate(iso: string, timezone?: string, withWeekday = true): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Assembled from parts: ICU versions disagree on a comma after the weekday.
  const parts = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: safeTimezone(timezone),
  }).formatToParts(d);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  const day = `${part("day")} ${part("month")} ${part("year")}`;
  return withWeekday ? `${part("weekday")} ${day}` : day;
}

const hasCoordinate = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== "";

/** Directions to the venue: the school's own map link, else its
 * coordinates, else the address as a search. */
export function mapsLink(address: ClassroomAddress): string {
  if (address.mapLink) return address.mapLink;
  const { lat, lng } = address.geolocation ?? {};
  if (hasCoordinate(lat) && hasCoordinate(lng)) return `https://www.google.com/maps?q=${lat},${lng}`;
  const query = [address.line1, address.line2, address.postcode ?? address.postCode, address.city, address.country || "United Kingdom"]
    .filter(Boolean)
    .join(" ");
  return `https://www.google.co.uk/maps/place/${encodeURIComponent(query)}`;
}

/**
 * "Add to calendar": a Google Calendar event for the occurrence, titled
 * "<class> with <school>", whose details link back to this booking.
 */
export function calendarLink(args: { title: string; schoolName: string; date: SessionDate; detailsUrl: string }): string | undefined {
  return googleCalendarLink({
    title: `${args.title} with ${args.schoolName}`,
    start: args.date.startDateTime,
    duration: args.date.duration || 0,
    details: `For more details go here: ${args.detailsUrl}`,
  });
}

/** "2 days and 3 hours". */
export function formatTimeBefore(days: number, hours: number): string {
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  return parts.join(" and ") || "0 hours";
}

/** The cancellation tiers, earliest notice first, each as a sentence. */
export function describeCancellationTiers(tiers: CancellationTier[]): string[] {
  return [...tiers]
    .sort((a, b) => (a.daysBefore === b.daysBefore ? b.hoursBefore - a.hoursBefore : b.daysBefore - a.daysBefore))
    .map(
      (t) =>
        `${formatTimeBefore(t.daysBefore, t.hoursBefore)} before the event: ${
          t.refundPercentage > 0 ? `${t.refundPercentage}% refund` : "No refund"
        }`,
    );
}

/** The reschedule rule as a sentence. */
export function describeReschedulePolicy(policy: BookingPolicy): string {
  if (!policy.canReschedule) return "This booking cannot be rescheduled.";
  return `You can reschedule your booking for another time at no extra cost, provided the event is not due to take place within the next ${formatTimeBefore(
    policy.rescheduleDaysBefore,
    policy.rescheduleHoursBefore,
  )}.`;
}

/** A ticket line's price: the full price, a legacy deposit ticket's deposit,
 * or a v2 deposit line's unit deposit next to its unit full price. */
export function ticketPrice(
  ticket: ReceiptTicket,
  breakdown: PricingBreakdownLine[] | undefined,
): { pence: number; fullPence?: number } {
  if (ticket.ticketTypeGroup === "deposit") return { pence: ticket.depositAmount ?? 0 };
  const key = String(ticket.ticketTypeId ?? ticket._id);
  const line = (breakdown ?? []).find((pb) => typeof pb.depositPence === "number" && String(pb.ticketTypeId) === key);
  if (!line) return { pence: ticket.price };
  // Both breakdown figures are line totals (unit × quantity), so the
  // division is exact; the row already says "count ×".
  const qty = Number(line.quantity) || Number(ticket.count) || 1;
  const fullPence = line.lineTotalPence != null ? Math.round(line.lineTotalPence / qty) : ticket.price;
  return { pence: Math.round((line.depositPence as number) / qty), fullPence };
}

/**
 * The receipt's total for a booking without a payment account. For a
 * ticketsv2 deposit booking `userCharged` is only the deposit taken, so the
 * total is that plus the balance, with the paid/owed split and when and how
 * the balance is collected.
 */
export function legacyTotals(booking: ClassroomBooking): {
  totalPence: number;
  deposit: null | { paidPence: number; remainingPence: number; note: string };
} {
  const charged = booking.accounting?.userCharged ?? 0;
  const remaining = booking.remainingBalance ?? booking.depositRemainder ?? 0;
  if (!(booking.paymentStatus === "deposit" && remaining > 0)) return { totalPence: charged, deposit: null };
  const due = booking.balanceDueDate ? `Balance due by ${longDate(booking.balanceDueDate, booking.timezone)}. ` : "";
  const how =
    booking.balanceCollection === "payment_link"
      ? "We'll email you a secure payment link."
      : booking.balanceCollection === "in_person"
        ? "Pay the balance at the venue."
        : "";
  return { totalPence: charged + remaining, deposit: { paidPence: charged, remainingPence: remaining, note: `${due}${how}`.trim() } };
}
